import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface IncomeW2Detail {
  id: string;
  employerName: string;
  wages: number;
  federalWithheld: number;
  stateWithheld: number;
}

export interface IncomeSEDetail {
  id: string;
  businessName: string;
  grossIncome: number;
  estimatedExpenses: number;
}

export interface IncomeInvestmentDetail {
  interestIncome: number;
  ordinaryDividends: number;
  qualifiedDividends: number;
  shortTermGains: number;
  longTermGains: number;
  capitalLosses: number;
}

export interface IncomeRentalDetail {
  numberOfProperties: number;
  grossRentalIncome: number;
  rentalExpenses: number;
}

export interface IncomeRetirementDetail {
  distributionAmount: number;
  accountType: 'traditional' | 'roth' | 'pension' | 'other';
}

export interface IncomeOtherDetail {
  id: string;
  description: string;
  amount: number;
}

export interface IncomeDetails {
  w2: IncomeW2Detail[];
  selfEmployment: IncomeSEDetail[];
  investment: IncomeInvestmentDetail;
  rental: IncomeRentalDetail;
  retirement: IncomeRetirementDetail;
  other: IncomeOtherDetail[];
}

export interface DeductionDetails {
  preference: 'standard' | 'itemized' | 'unsure';
  itemized: {
    mortgageInterest: number;
    stateLocalTaxes: number;
    charitableContributions: number;
    medicalExpenses: number;
  };
  adjustments: {
    studentLoanInterest: number;
    educatorExpenses: number;
    hsaContributions: number;
    hsaType: 'individual' | 'family';
    traditionalIra: number;
    seHealthInsurance: number;
  };
}

export interface TaxPayments {
  estimatedPayments: {
    q1: number;
    q2: number;
    q3: number;
    q4: number;
  };
  priorYearOverpayment: number;
}

export interface QuestionnaireAnswers {
  filingStatus?: string;
  personalInfo?: {
    name: string;
    ssn: string;
    dateOfBirth: string;
    occupation: string;
    stateOfResidence: string;
    spouseInfo?: {
      name: string;
      ssn: string;
      dateOfBirth: string;
      occupation: string;
    };
  };
  dependents?: Array<{
    id: string;
    name: string;
    ssn: string;
    relationship: string;
    dateOfBirth: string;
  }>;
  incomeSources?: string[];
  incomeDetails?: IncomeDetails;
  deductionsAndCredits?: string[];
  deductionDetails?: DeductionDetails;
  taxPayments?: TaxPayments;
  priorYearReturnId?: number;
  lifeEvents?: string[];
}

export interface IncomeSourceDocument {
  id: number;
  issuer: string;
  amount: number;
  recipientName?: string;
}

export interface IncomeSourceDetected {
  type: string;
  detected: boolean;
  documents: IncomeSourceDocument[];
}

export interface SmartDefaults {
  suggestedFilingStatus: string;
  filingStatusReason: string;
  detectedTaxYear: number | null;
  taxYearWarning: string | null;
  primaryFiler: { name: string | null; state: string | null };
  spouseDetected: { name: string } | null;
  incomeSources: IncomeSourceDetected[];
  totalWithholding: number;
  stateWithholding: number;
  hsaContributions: number | null;
  retirementContributions: number | null;
  estimatedPayments: { q1: number; q2: number; q3: number; q4: number } | null;
  priorYearOverpayment: number | null;
  documentsStatus: { total: number; analyzed: number; pending: number };
}

export interface QuestionnaireState {
  currentStep: number;
  totalSteps: number;
  answers: QuestionnaireAnswers;
  smartDefaults: SmartDefaults | null;
  smartDefaultsLoading: boolean;
  fieldOverrides: Record<string, boolean>;
  isComplete: boolean;
  isSubmitting: boolean;
  error: string | null;
}

const initialState: QuestionnaireState = {
  currentStep: 1,
  totalSteps: 5,
  answers: {},
  smartDefaults: null,
  smartDefaultsLoading: false,
  fieldOverrides: {},
  isComplete: false,
  isSubmitting: false,
  error: null,
};

const questionnaireSlice = createSlice({
  name: 'questionnaire',
  initialState,
  reducers: {
    setStep: (state, action: PayloadAction<number>) => {
      if (action.payload >= 1 && action.payload <= state.totalSteps) {
        state.currentStep = action.payload;
      }
    },

    nextStep: (state) => {
      if (state.currentStep < state.totalSteps) {
        state.currentStep += 1;
      }
    },

    previousStep: (state) => {
      if (state.currentStep > 1) {
        state.currentStep -= 1;
      }
    },

    updateAnswers: (state, action: PayloadAction<Partial<QuestionnaireAnswers>>) => {
      state.answers = { ...state.answers, ...action.payload };
    },

    updateFilingStatus: (state, action: PayloadAction<string>) => {
      state.answers.filingStatus = action.payload;
    },

    updatePersonalInfo: (
      state,
      action: PayloadAction<{
        name: string;
        ssn: string;
        dateOfBirth: string;
        occupation: string;
        stateOfResidence?: string;
      }>
    ) => {
      state.answers.personalInfo = { ...state.answers.personalInfo, stateOfResidence: '', ...action.payload };
    },

    addDependent: (
      state,
      action: PayloadAction<{
        id: string;
        name: string;
        ssn: string;
        relationship: string;
        dateOfBirth: string;
      }>
    ) => {
      if (!state.answers.dependents) {
        state.answers.dependents = [];
      }
      state.answers.dependents.push(action.payload);
    },

    removeDependent: (state, action: PayloadAction<string>) => {
      if (state.answers.dependents) {
        state.answers.dependents = state.answers.dependents.filter((d) => d.id !== action.payload);
      }
    },

    updateIncomeSources: (state, action: PayloadAction<string[]>) => {
      state.answers.incomeSources = action.payload;
    },

    updateDeductionsAndCredits: (state, action: PayloadAction<string[]>) => {
      state.answers.deductionsAndCredits = action.payload;
    },

    setIsSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },

    setComplete: (state, action: PayloadAction<boolean>) => {
      state.isComplete = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    resetQuestionnaire: (state) => {
      state.currentStep = 1;
      state.answers = {};
      state.isComplete = false;
      state.isSubmitting = false;
      state.error = null;
    },

    addW2: (state, action: PayloadAction<IncomeW2Detail>) => {
      if (!state.answers.incomeDetails) {
        state.answers.incomeDetails = {
          w2: [], selfEmployment: [], investment: { interestIncome: 0, ordinaryDividends: 0, qualifiedDividends: 0, shortTermGains: 0, longTermGains: 0, capitalLosses: 0 },
          rental: { numberOfProperties: 0, grossRentalIncome: 0, rentalExpenses: 0 },
          retirement: { distributionAmount: 0, accountType: 'traditional' },
          other: []
        };
      }
      state.answers.incomeDetails.w2.push(action.payload);
    },

    removeW2: (state, action: PayloadAction<string>) => {
      if (state.answers.incomeDetails) {
        state.answers.incomeDetails.w2 = state.answers.incomeDetails.w2.filter(w => w.id !== action.payload);
      }
    },

    updateW2: (state, action: PayloadAction<IncomeW2Detail>) => {
      if (state.answers.incomeDetails) {
        const idx = state.answers.incomeDetails.w2.findIndex(w => w.id === action.payload.id);
        if (idx >= 0) state.answers.incomeDetails.w2[idx] = action.payload;
      }
    },

    addSEIncome: (state, action: PayloadAction<IncomeSEDetail>) => {
      if (!state.answers.incomeDetails) {
        state.answers.incomeDetails = {
          w2: [], selfEmployment: [], investment: { interestIncome: 0, ordinaryDividends: 0, qualifiedDividends: 0, shortTermGains: 0, longTermGains: 0, capitalLosses: 0 },
          rental: { numberOfProperties: 0, grossRentalIncome: 0, rentalExpenses: 0 },
          retirement: { distributionAmount: 0, accountType: 'traditional' },
          other: []
        };
      }
      state.answers.incomeDetails.selfEmployment.push(action.payload);
    },

    removeSEIncome: (state, action: PayloadAction<string>) => {
      if (state.answers.incomeDetails) {
        state.answers.incomeDetails.selfEmployment = state.answers.incomeDetails.selfEmployment.filter(s => s.id !== action.payload);
      }
    },

    updateIncomeDetails: (state, action: PayloadAction<Partial<IncomeDetails>>) => {
      if (!state.answers.incomeDetails) {
        state.answers.incomeDetails = {
          w2: [], selfEmployment: [], investment: { interestIncome: 0, ordinaryDividends: 0, qualifiedDividends: 0, shortTermGains: 0, longTermGains: 0, capitalLosses: 0 },
          rental: { numberOfProperties: 0, grossRentalIncome: 0, rentalExpenses: 0 },
          retirement: { distributionAmount: 0, accountType: 'traditional' },
          other: []
        };
      }
      Object.assign(state.answers.incomeDetails, action.payload);
    },

    updateDeductionDetails: (state, action: PayloadAction<Partial<DeductionDetails>>) => {
      if (!state.answers.deductionDetails) {
        state.answers.deductionDetails = {
          preference: 'unsure',
          itemized: { mortgageInterest: 0, stateLocalTaxes: 0, charitableContributions: 0, medicalExpenses: 0 },
          adjustments: { studentLoanInterest: 0, educatorExpenses: 0, hsaContributions: 0, hsaType: 'individual', traditionalIra: 0, seHealthInsurance: 0 }
        };
      }
      if (action.payload.preference !== undefined) state.answers.deductionDetails.preference = action.payload.preference;
      if (action.payload.itemized) Object.assign(state.answers.deductionDetails.itemized, action.payload.itemized);
      if (action.payload.adjustments) Object.assign(state.answers.deductionDetails.adjustments, action.payload.adjustments);
    },

    updateTaxPayments: (state, action: PayloadAction<Partial<TaxPayments>>) => {
      if (!state.answers.taxPayments) {
        state.answers.taxPayments = {
          estimatedPayments: { q1: 0, q2: 0, q3: 0, q4: 0 },
          priorYearOverpayment: 0
        };
      }
      if (action.payload.estimatedPayments) Object.assign(state.answers.taxPayments.estimatedPayments, action.payload.estimatedPayments);
      if (action.payload.priorYearOverpayment !== undefined) state.answers.taxPayments.priorYearOverpayment = action.payload.priorYearOverpayment;
    },

    setPriorYearReturnId: (state, action: PayloadAction<number | undefined>) => {
      state.answers.priorYearReturnId = action.payload;
    },

    updateLifeEvents: (state, action: PayloadAction<string[]>) => {
      state.answers.lifeEvents = action.payload;
    },

    setSmartDefaults: (state, action: PayloadAction<SmartDefaults>) => {
      state.smartDefaults = action.payload;
      state.smartDefaultsLoading = false;
    },

    setSmartDefaultsLoading: (state, action: PayloadAction<boolean>) => {
      state.smartDefaultsLoading = action.payload;
    },

    markFieldOverride: (state, action: PayloadAction<string>) => {
      state.fieldOverrides[action.payload] = true;
    },

    applySmartDefaults: (state) => {
      const sd = state.smartDefaults;
      if (!sd) return;

      // Only apply to fields not overridden by user
      if (!state.fieldOverrides['filingStatus'] && sd.suggestedFilingStatus) {
        state.answers.filingStatus = sd.suggestedFilingStatus;
      }
      if (!state.fieldOverrides['personalInfo'] && sd.primaryFiler.name) {
        if (!state.answers.personalInfo) {
          state.answers.personalInfo = { name: '', ssn: '', dateOfBirth: '', occupation: '', stateOfResidence: '' };
        }
        state.answers.personalInfo.name = sd.primaryFiler.name;
        if (sd.primaryFiler.state) {
          state.answers.personalInfo.stateOfResidence = sd.primaryFiler.state;
        }
        if (sd.spouseDetected?.name) {
          state.answers.personalInfo.spouseInfo = {
            name: sd.spouseDetected.name,
            ssn: '',
            dateOfBirth: '',
            occupation: '',
          };
        }
      }
      if (!state.fieldOverrides['hsaContributions'] && sd.hsaContributions) {
        if (!state.answers.deductionDetails) {
          state.answers.deductionDetails = {
            preference: 'unsure',
            itemized: { mortgageInterest: 0, stateLocalTaxes: 0, charitableContributions: 0, medicalExpenses: 0 },
            adjustments: { studentLoanInterest: 0, educatorExpenses: 0, hsaContributions: 0, hsaType: 'individual', traditionalIra: 0, seHealthInsurance: 0 },
          };
        }
        state.answers.deductionDetails.adjustments.hsaContributions = sd.hsaContributions;
      }
    },
  },
});

export const {
  setStep,
  nextStep,
  previousStep,
  updateAnswers,
  updateFilingStatus,
  updatePersonalInfo,
  addDependent,
  removeDependent,
  updateIncomeSources,
  updateDeductionsAndCredits,
  setIsSubmitting,
  setComplete,
  setError,
  resetQuestionnaire,
  addW2,
  removeW2,
  updateW2,
  addSEIncome,
  removeSEIncome,
  updateIncomeDetails,
  updateDeductionDetails,
  updateTaxPayments,
  setPriorYearReturnId,
  updateLifeEvents,
  setSmartDefaults,
  setSmartDefaultsLoading,
  markFieldOverride,
  applySmartDefaults,
} = questionnaireSlice.actions;

export default questionnaireSlice.reducer;
